import RawClient from "clients/RawClient";
import HeapHelpers from "helpers/heap-helpers";
const PIXEL_URL = "/pixel/p.php";
const beaconSupported = () => !!navigator.sendBeacon;

/**
 * This file contains the specific client code to emit events to our various analytics platforms
 * In a typescript world we would define a type here:
 *
 * type AnalyticsPipe: (data: Record<string, any>) => void
 *
 * Add a new analytics integration here if the integration needs a stream of events from the entire application, and add it to analyticsIntegrationPipes
 */

/**
 * Creates a new object with 'pixel_' prepended to every key of the input object.
 * @param {object} obj The original object.
 * @returns {object} A new object with prefixed keys.
 */
const addPixelPrefix = obj => {
  return Object.fromEntries(Object.entries(obj).map(([key, value]) => [`pixel_${key}`, value]));
};

/**
 * Dual write pixel to heap under the event name rtrpixel
 * @param {object} data the data to send
 * @returns {void} - just calls HeapHelpers.fireHeapEvent()
 */

export function dualWritePixelToHeap(data) {
  const dataClone = { ...data };
  Object.entries(dataClone).forEach(([key, value]) => {
    // Heap can only accept (string | array | number | boolean) as values
    // so if we pass a different data type (like a nested object) it will throw an error
    // and prevent the event from being logged. So we need to convert these values to strings
    // docs: https://developers.heap.io/reference/track
    if (typeof value === "object" && !Array.isArray(value)) {
      try {
        dataClone[key] = JSON.stringify(value);
      } catch (e) {
        // if we can't stringify it, mark it as such
        dataClone[key] = "[value cannot be stringified]";
      }
    }
  });
  HeapHelpers.fireHeapEvent("rtr_pixel", addPixelPrefix(dataClone));
}

/**
 * Send data to RTR pixel
 * @param data The data to send
 */
export function sendPixel(data) {
  let beaconQueuedSuccessfully = false;

  if (beaconSupported()) {
    // FormData causes the browser to send Content-Type: multipart/form-data which our pixel server does not like.
    // URLSearchParams, however, correctly sets Content-Type: application/x-www-form-urlencoded
    const paramsData = new URLSearchParams();
    paramsData.append("sendbeacon_status", "success"); // This object is only used by beacon, so mark as success. We'll override this key if it fails.
    Object.keys(data).forEach(function (key) {
      paramsData.append(key, data[key]);
    });
    // navigator.sendBeacon is present in all modern browsers except IE. It makes a POST request (with cookies) much
    // like $.post, with the major exception that (a) the requests are of the LOWEST priority and (b) are guaranteed to
    // fire even in the event of a page unload.
    // navigator.sendBeacon returns true/false based on whether or not the browser successfully QUEUED the request.
    // @see https://developer.mozilla.org/en-US/docs/Web/API/Navigator/sendBeacon

    // Chrome does not correctly set the payload content-type, so we need to manually control via Blob
    const beaconPayload = new Blob([paramsData.toString()], {
      type: "application/x-www-form-urlencoded;charset=UTF-8",
    });
    beaconQueuedSuccessfully = window.navigator.sendBeacon(PIXEL_URL, beaconPayload);
  }

  if (!beaconQueuedSuccessfully) {
    if (!beaconSupported()) {
      data["sendbeacon_status"] = "unsupported";
    } else {
      data["sendbeacon_status"] = "failure";
    }

    RawClient.post({
      url: PIXEL_URL,
      body: JSON.stringify(data),
    });
  }
}

// intentionally excluding dualWritePixelToHeap here to wait for the LD flag to be loaded
// if we can remove this LD flag in the future, we can add dualWritePixelToHeap here
// and remove the queuing logic from analytics-client.js
const analyticsIntegrationPipes = [sendPixel];

export default analyticsIntegrationPipes;
